<template>
  <YapNav :logo-route-to-name="RouteNames.Transactions" :dropdown-entries="state.dropdownEntries">
    <template #right>
      <YapButtonIcon @click="toggleHiddenAmount">
        <YapIcon name="eye" class="h-6 w-6" v-if="profileStore.amountVisible" />
        <YapIcon name="eye-off" class="h-6 w-6" v-else />
      </YapButtonIcon>
      <YapSse />
    </template>
  </YapNav>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue';

import router from '@/router/router';
import YapSse from '@/components/YapSse.vue';
import YapIcon from '@/components/YapIcon.vue';
import { useUserStore } from '@/store/userStore';
import { Partnership, PartnershipStatus } from '@/types/partnership';
import { useProfileStore } from '@/store/profileStore';
import { ContextType, RouteNames } from '@/router/routes';
import YapButtonIcon from '@/components/YapButtonIcon.vue';
import { usePartnershipsStore } from '@/store/partnershipsStore';
import { useContextSwitchStore } from '@/store/contextSwitchStore';
import { logoutAndResetAllStoresWithCid } from '@/store/storeUtils';
import { DropdownGroup } from '@/components/YapDropdown.vue';
import { useSuggestionsStore } from '@/store/suggestionsStore';
import { storeToRefs } from 'pinia';
import { useExportsStore } from '@/store/exportsStore';
import YapNav from '@/components/navigation/YapNav.vue';

interface State {
  hasActivateEbillSuggestion: boolean;
  hasEbillSettings: boolean;
  displayDocumentExports: boolean;
  currentPartnership?: Partnership;
  dropdownEntries: Array<DropdownGroup & { displayCondition?: boolean }>;
}

const userStore = useUserStore();
const suggestionsStore = useSuggestionsStore();
const partnershipsStore = usePartnershipsStore();
const contextSwitchStore = useContextSwitchStore();
const exportsStore = useExportsStore();
const { dataSharingExports } = storeToRefs(exportsStore);

const state: State = reactive({
  hasActivateEbillSuggestion: computed(() => suggestionsStore.hasActivateEbillSuggestion()),
  hasEbillSettings: computed(() => {
    return (
      !state.hasActivateEbillSuggestion &&
      (contextSwitchStore.isPrivateContext
        ? userStore.hasEbillActivated
        : partnershipsStore.hasEbillActivatedForCurrentPartnership)
    );
  }),
  displayDocumentExports: computed(() => {
    if (contextSwitchStore.getContextType === ContextType.Private) return true;
    return (
      dataSharingExports.value.length > 0 &&
      (contextSwitchStore.getContextType === ContextType.Partnership ||
        contextSwitchStore.getContextType === ContextType.Registered)
    );
  }),
  currentPartnership: computed(() => partnershipsStore.currentPartnership),
  dropdownEntries: computed(() => {
    return [
      {
        id: 'part1',
        items: [
          {
            id: 'personal-details',
            name: 'app.settings.personalDetails.title',
            icon: 'profile-outline',
            to: { name: RouteNames.PersonalDetails },
            meta: { contexts: [ContextType.Private] }
          },
          {
            id: 'partnership-details',
            name: 'app.settings.partnershipDetails.title',
            icon: 'home',
            to: { name: RouteNames.PartnershipDetails },
            meta: { contexts: [ContextType.Partnership] },
            displayCondition: !partnershipsStore.isRequestorBootstrapperForCurrentPartnership
          },
          {
            id: 'settings',
            name: 'app.settings.security.title',
            icon: 'lock-outline',
            meta: { contexts: [ContextType.Private, ContextType.Partnership, ContextType.Registered] },
            to: { name: RouteNames.Settings }
          },
          {
            id: 'legal',
            name: 'app.settings.legal.title',
            icon: 'legal-outline',
            meta: { contexts: [ContextType.Private, ContextType.Partnership, ContextType.Registered] },
            to: { name: RouteNames.Legal }
          },
          {
            id: 'documents',
            name: 'app.settings.documents.title',
            icon: 'export-outline',
            to: { name: RouteNames.Documents },
            meta: { contexts: [ContextType.Private, ContextType.Partnership, ContextType.Registered] },
            displayCondition: state.displayDocumentExports
          },
          {
            id: 'subscription',
            name: 'app.settings.partnershipConditions.title',
            icon: 'subscription-outline',
            to: { name: RouteNames.SmeSubscription },
            meta: { contexts: [ContextType.Partnership] },
            displayCondition:
              state.currentPartnership?.status === PartnershipStatus.Bankable &&
              !partnershipsStore.isRequestorBootstrapperForCurrentPartnership
          },
          {
            id: 'ebill-activate',
            name: 'app.home.navigation.activateEbill',
            icon: 'ebill-outline',
            to: { name: RouteNames.ActivateEbill },
            meta: { contexts: [ContextType.Private, ContextType.Partnership] },
            displayCondition: state.hasActivateEbillSuggestion
          },
          {
            id: 'ebill-settings',
            name: 'app.settings.ebill.title',
            icon: 'ebill-outline',
            to: { name: RouteNames.EbillSettings },
            meta: { contexts: [ContextType.Private, ContextType.Partnership] },
            displayCondition: state.hasEbillSettings
          }
        ].filter(
          (item) =>
            item.meta?.contexts.includes(contextSwitchStore.getContextType) &&
            (item.displayCondition === undefined || item.displayCondition)
        )
      },
      {
        id: 'part2',
        items: [
          {
            id: 'logout',
            name: 'app.settings.logout.title',
            icon: 'logout-outline',
            action: logout
          }
        ]
      }
    ];
  })
});

function logout() {
  logoutAndResetAllStoresWithCid();
  router.replace({ name: RouteNames.Login });
}

const profileStore = useProfileStore();
function toggleHiddenAmount() {
  profileStore.setAmountVisible(!profileStore.amountVisible);
}
</script>
